/**
 * section-knowledge.tsx — Mục "Kiến thức" của màn nhân viên AI (Cần phát hành):
 *   - KHO TRI THỨC nhân viên AI được tra (`search_knowledge.guardrail.knowledgeBaseIds`; không chọn kho nào = tra TẤT CẢ kho).
 *     "Chọn kho tri thức" → popup chọn nhiều kho; "Quản lý kho tri thức" / "Tạo kho" / "Sửa" mở trang Kho tri thức ở
 *     TAB MỚI (màn này giữ nguyên) + nút "Tải lại" danh sách kho (cũng tự làm mới khi quay lại tab).
 *   - Độ khớp khi tra (hiện số thật máy chủ dùng — mặc định 0,5), viết lại câu hỏi; "Nguồn dữ liệu bán hàng".
 */
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { BookOpen, Database, FolderOpen, Pencil, Plus, Save, Search, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/misc'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { useKnowledgeBases, KB_BASES_KEY, type KnowledgeBase } from '@/hooks/use-knowledge-bases'
import { OpenInNewTab, openInNewTab } from '@/components/ai/open-in-new-tab'
import { ReloadButton } from '@/components/ai/reload-button'
import { baseCountsLabel, KNOWLEDGE_BASES_PATH, knowledgeBaseUrl, newKnowledgeBaseUrl } from '../knowledge/kb-lib'
import { useKnowledgePerms } from '../knowledge/knowledge-bases-page'
import { useProductCategories } from '@/hooks/use-products'
import { cn } from '@/lib/utils'
import { currentLocale } from '@/lib/i18n'
import { DataSourcesBlock } from '../ai-data-sources-block'
import { CreateKnowledgeDialog } from '../add-knowledge-dialog'
import { SettingRow } from '../train/train-ui'
import { StaffPanel } from './staff-ui'
import { RETRIEVAL_OPTIONS, retrievalValue } from './staff-lib'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { patchTool, toolCategoryIds, toolEnabled, toolKnowledgeBaseIds, type SectionProps } from '../train/draft-form'

export const KNOWLEDGE_PAGE = KNOWLEDGE_BASES_PATH

function countsLabel(c: KnowledgeBase): string {
  return baseCountsLabel(c)
}

export function KnowledgeSection({ form, set, setSettings, canEdit }: SectionProps) {
  const { t } = useTranslation('aiStaff')
  const { data: kbCats, isLoading } = useKnowledgeBases()
  const { data: prodCats } = useProductCategories()
  // "Thêm tài liệu" ghi vào Kho tri thức (POST /knowledge) → backend đòi `knowledge.create`, không phải `ai.update`.
  const { canCreate: canAddDoc } = useKnowledgePerms()
  const [addOpen, setAddOpen] = useState(false)
  const [pickOpen, setPickOpen] = useState(false)
  const [prodLimitOpen, setProdLimitOpen] = useState(false)
  const tools = form.toolsJson
  const kbOn = toolEnabled(tools, 'search_knowledge')
  const kbIds = toolKnowledgeBaseIds(tools)
  const cats = kbCats ?? []
  const limited = kbIds.length > 0
  const attached = limited ? cats.filter((c) => kbIds.includes(c.id)) : cats
  const setIds = (ids: string[]) => set({ toolsJson: patchTool(tools, 'search_knowledge', { knowledgeBaseIds: ids }) })
  const detach = (id: string) => setIds((limited ? kbIds : cats.map((c) => c.id)).filter((x) => x !== id))

  return (
    <StaffPanel
      id="staff-kien-thuc"
      title={t('knowledge.title')}
      icon={<BookOpen className="h-4 w-4" />}
      scope="publish"
      description={t('knowledge.description')}
      actions={
        <>
          {canEdit && <Button size="sm" variant="outline" onClick={() => setPickOpen(true)} title={t('knowledge.pickTitle')}><Plus className="h-3.5 w-3.5" /> {t('knowledge.pick')}</Button>}
          <OpenInNewTab to={KNOWLEDGE_PAGE} variant="ghost" size="sm" title={t('knowledge.openPageTitle')}><FolderOpen className="h-3.5 w-3.5" /> {t('knowledge.manage')}</OpenInNewTab>
          <ReloadButton queryKeys={[KB_BASES_KEY]} what={t('knowledge.reloadWhat')} iconOnly />
        </>
      }
    >
      <div className="space-y-2">
        <div className="flex items-start gap-3">
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium">{t('knowledge.searched.title')}</p>
            <p className="text-xs text-muted-foreground">
              {t('knowledge.searched.hint')} {limited ? t('knowledge.searched.limited', { count: attached.length }) : t('knowledge.searched.unlimited')} {t('knowledge.searched.off')}
            </p>
          </div>
          <Switch checked={kbOn} onCheckedChange={(v) => set({ toolsJson: patchTool(tools, 'search_knowledge', { enabled: v }) })} disabled={!canEdit} aria-label={t('knowledge.searched.aria')} />
        </div>
        {kbOn && (
          cats.length === 0 && !isLoading ? (
            <div className="rounded-lg border border-dashed p-5 text-center">
              <FolderOpen className="mx-auto h-6 w-6 text-muted-foreground" />
              <p className="mt-2 text-sm font-medium">{t('knowledge.empty.title')}</p>
              <p className="mt-1 text-xs text-muted-foreground">{t('knowledge.empty.hint')}</p>
              <div className="mt-3 flex flex-wrap items-center justify-center gap-2">
                {canAddDoc && <Button size="sm" onClick={() => setAddOpen(true)}><Plus className="h-3.5 w-3.5" /> {t('knowledge.addDoc')}</Button>}
                <OpenInNewTab to={newKnowledgeBaseUrl(`Kiến thức — ${form.name}`) /* i18n-ignore: tên kho mặc định, lưu thành dữ liệu */} variant="outline" size="sm"><FolderOpen className="h-3.5 w-3.5" /> {t('knowledge.createBase')}</OpenInNewTab>
                <ReloadButton queryKeys={[KB_BASES_KEY]} what={t('knowledge.reloadWhat')} />
              </div>
            </div>
          ) : (
            <ul className="divide-y rounded-lg border">
              {attached.map((c) => (
                <li key={c.id} className="group flex min-h-[48px] items-center gap-3 px-3 py-2 hover:bg-muted/40">
                  <span className="text-base">📚</span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">{c.name}</p>
                    <p className="text-[11px] text-muted-foreground">{countsLabel(c)}</p>
                  </div>
                  {!limited && <Badge variant="success" className="shrink-0 text-[10px]">{t('knowledge.searchedBadge')}</Badge>}
                  <div className="flex shrink-0 items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
                    <Button size="icon" variant="ghost" className="h-7 w-7" title={t('knowledge.editBaseTitle')} aria-label={t('knowledge.editBaseAria', { name: c.name })} onClick={() => openInNewTab(knowledgeBaseUrl(c.id))}><Pencil className="h-3.5 w-3.5" /></Button>
                    {canEdit && <Button size="icon" variant="ghost" className="h-7 w-7" title={t('knowledge.detachTitle')} aria-label={t('knowledge.detachAria', { name: c.name })} disabled={attached.length <= 1 && limited} onClick={() => detach(c.id)}><X className="h-3.5 w-3.5 text-destructive" /></Button>}
                  </div>
                </li>
              ))}
              {attached.length === 0 && <li className="px-3 py-3 text-center text-xs text-muted-foreground">{t('knowledge.missingBases')}</li>}
              {limited && (
                <li className="flex items-center justify-between px-3 py-1.5 text-[11px] text-muted-foreground">
                  <span>{t('knowledge.hardLimit')}</span>
                  {canEdit && <button type="button" className="text-primary hover:underline" onClick={() => setIds([])}>{t('knowledge.searchAll')}</button>}
                </li>
              )}
            </ul>
          )
        )}
        {kbOn && cats.length > 0 && canAddDoc && (
          <div className="flex justify-end">
            <Button size="sm" variant="ghost" onClick={() => setAddOpen(true)}><Plus className="h-3.5 w-3.5" /> {t('knowledge.addDocToBase')}</Button>
          </div>
        )}
      </div>

      <div className="grid gap-3 rounded-lg border p-3 sm:grid-cols-2">
        <div className="space-y-1">
          <p className="text-sm font-medium">{t('knowledge.threshold.title')}</p>
          <p className="text-xs text-muted-foreground">{t('knowledge.threshold.hint')}</p>
          <Select
            value={String(retrievalValue(form.settings.retrievalThreshold))}
            onValueChange={(v) => setSettings((x) => ({ ...x, retrievalThreshold: Number(v) }))}
            disabled={!canEdit}
          >
            <SelectTrigger className="h-8" aria-label={t('knowledge.threshold.title')}><SelectValue /></SelectTrigger>
            <SelectContent>
              {!RETRIEVAL_OPTIONS.some((o) => o.value === retrievalValue(form.settings.retrievalThreshold)) && (
                <SelectItem value={String(retrievalValue(form.settings.retrievalThreshold))}>
                  {t('knowledge.threshold.current', { value: retrievalValue(form.settings.retrievalThreshold).toLocaleString(currentLocale()) })}
                </SelectItem>
              )}
              {RETRIEVAL_OPTIONS.map((o) => <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <SettingRow
          title={t('knowledge.rewrite.title')}
          description={t('knowledge.rewrite.description')}
          checked={form.settings.rewriteQuery}
          onChange={(v) => setSettings((x) => ({ ...x, rewriteQuery: v }))}
          disabled={!canEdit}
        />
      </div>

      <div className="space-y-2">
        <p className="text-sm font-medium">{t('knowledge.salesData')}</p>
        <DataSourcesBlock
          tools={tools}
          onChange={(next) => set({ toolsJson: next })}
          canEdit={canEdit}
          categoryLimitCount={toolCategoryIds(tools, 'search_products').length}
          onOpenCategoryLimit={() => setProdLimitOpen(true)}
        />
      </div>

      {pickOpen && (
        <KnowledgePickerDialog
          categories={cats}
          selected={limited ? kbIds : cats.map((c) => c.id)}
          allMode={!limited}
          canEdit={canEdit}
          onClose={() => setPickOpen(false)}
          onConfirm={(ids) => { setIds(ids.length === cats.length ? [] : ids); setPickOpen(false) }}
        />
      )}
      {addOpen && (
        <CreateKnowledgeDialog
          defaultKnowledgeBaseId={limited ? attached[0]?.id ?? '' : ''}
          onClose={() => setAddOpen(false)}
          onTryChat={() => document.getElementById('demo-chat-input')?.focus()}
        />
      )}
      {prodLimitOpen && (
        <CategoryLimitDialog
          categories={(prodCats ?? []).map((c: { id: string; name: string }) => ({ id: c.id, name: c.name }))}
          selected={toolCategoryIds(tools, 'search_products')}
          canEdit={canEdit}
          onClose={() => setProdLimitOpen(false)}
          onSave={(ids) => { set({ toolsJson: patchTool(tools, 'search_products', { categoryIds: ids }) }); setProdLimitOpen(false) }}
        />
      )}
    </StaffPanel>
  )
}

/** Popup "Chọn kho tri thức": liệt kê kho của tổ chức + số tài liệu / hỏi đáp, tìm kiếm, tick nhiều, Xác nhận. */
function KnowledgePickerDialog({ categories, selected, allMode, canEdit, onClose, onConfirm }: {
  categories: KnowledgeBase[]
  selected: string[]
  allMode: boolean
  canEdit: boolean
  onClose: () => void
  onConfirm: (ids: string[]) => void
}) {
  const { t } = useTranslation('aiStaff')
  const [ids, setIds] = useState<string[]>(selected)
  const [q, setQ] = useState('')
  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase()
    return needle ? categories.filter((c) => c.name.toLowerCase().includes(needle)) : categories
  }, [categories, q])
  const toggle = (id: string) => setIds((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]))
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><BookOpen className="h-4 w-4 text-primary" /> {t('knowledge.pick')}</DialogTitle>
          <DialogDescription>{t('knowledge.picker.description')}</DialogDescription>
        </DialogHeader>
        {categories.length === 0 ? (
          <div className="rounded-lg border border-dashed p-6 text-center">
            <FolderOpen className="mx-auto h-7 w-7 text-muted-foreground" />
            <p className="mt-2 text-sm font-medium">{t('knowledge.picker.noBases')}</p>
            <p className="mt-1 text-xs text-muted-foreground">{t('knowledge.picker.noBasesHint')}</p>
            <div className="mt-3 flex flex-wrap items-center justify-center gap-2">
              <OpenInNewTab to={newKnowledgeBaseUrl()} variant="default" size="sm"><FolderOpen className="h-3.5 w-3.5" /> {t('knowledge.createBase')}</OpenInNewTab>
              <ReloadButton queryKeys={[KB_BASES_KEY]} what={t('knowledge.reloadWhat')} />
            </div>
          </div>
        ) : (
          <>
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input autoFocus className="h-8 pl-8 text-sm" placeholder={t('knowledge.picker.search')} value={q} onChange={(e) => setQ(e.target.value)} />
            </div>
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>{t('knowledge.picker.selected', { n: ids.length, total: categories.length })}{allMode && ids.length === categories.length ? t('knowledge.picker.all') : ''}</span>
              <span className="flex gap-2">
                <button type="button" className="text-primary hover:underline" onClick={() => setIds(categories.map((c) => c.id))}>{t('common:actions.selectAll')}</button>
                <button type="button" className="text-primary hover:underline" onClick={() => setIds([])}>{t('knowledge.picker.deselect')}</button>
              </span>
            </div>
            <ul className="max-h-80 divide-y overflow-y-auto rounded-lg border">
              {shown.map((c) => (
                <li key={c.id}>
                  <label className={cn('flex cursor-pointer items-center gap-3 px-3 py-2 hover:bg-muted/50', ids.includes(c.id) && 'bg-primary/5')}>
                    <input type="checkbox" checked={ids.includes(c.id)} onChange={() => toggle(c.id)} disabled={!canEdit} />
                    <span className="text-base">📚</span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium">{c.name}</span>
                      <span className="block text-[11px] text-muted-foreground">{countsLabel(c)}</span>
                    </span>
                  </label>
                </li>
              ))}
              {shown.length === 0 && <li className="p-3 text-center text-xs text-muted-foreground">{t('knowledge.picker.noMatch', { q })}</li>}
            </ul>
            <div className="flex items-center justify-between">
              <span className="flex flex-wrap items-center gap-1">
                <OpenInNewTab to={KNOWLEDGE_PAGE} variant="ghost" size="sm" title={t('knowledge.openPageTitle')}><FolderOpen className="h-3.5 w-3.5" /> {t('knowledge.manage')}</OpenInNewTab>
                <ReloadButton queryKeys={[KB_BASES_KEY]} what={t('knowledge.reloadWhat')} iconOnly />
              </span>
              <div className="flex gap-2">
                <Button variant="ghost" onClick={onClose}>{t('common:actions.cancel')}</Button>
                {canEdit && <Button onClick={() => onConfirm(ids)} disabled={ids.length === 0} title={ids.length === 0 ? t('knowledge.picker.atLeastOne') : undefined}>{t('common:actions.confirm')}</Button>}
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}

/** Chọn nhóm sản phẩm AI được tra (không chọn = tất cả). */
function CategoryLimitDialog({ categories, selected, canEdit, onClose, onSave }: {
  categories: Array<{ id: string; name: string }>
  selected: string[]
  canEdit: boolean
  onClose: () => void
  onSave: (ids: string[]) => void
}) {
  const { t } = useTranslation('aiStaff')
  const [ids, setIds] = useState<string[]>(selected)
  const toggle = (id: string) => setIds((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]))
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Database className="h-4 w-4 text-primary" /> {t('knowledge.category.title')}</DialogTitle>
          <DialogDescription>{t('knowledge.category.description')}</DialogDescription>
        </DialogHeader>
        <div className="max-h-72 space-y-1 overflow-y-auto rounded-lg border p-2">
          {categories.length === 0 ? (
            <p className="p-2 text-xs text-muted-foreground">{t('knowledge.category.none')}</p>
          ) : categories.map((c) => (
            <label key={c.id} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-muted">
              <input type="checkbox" checked={ids.includes(c.id)} onChange={() => toggle(c.id)} disabled={!canEdit} />
              <span className="truncate">{c.name}</span>
            </label>
          ))}
        </div>
        {canEdit && (
          <div className="flex justify-between">
            <Button variant="ghost" onClick={() => setIds([])} disabled={ids.length === 0}>{t('knowledge.category.clear')}</Button>
            <Button onClick={() => onSave(ids)}><Save className="h-4 w-4" /> {t('common:actions.done')}</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
